<template>
  <div v-if="authorData.title" class="block-author">
    <div v-if="authorData.image" class="block-author__image">
      <AtomImage :src="authorData.image" />
    </div>
    <div class="block-author__content" :class="!authorData.image && 'is-full-width'">
      <AtomHeading
        v-if="authorData.title"
        tag="h2"
        appereance="h2"
        :text="authorData.title"
      />
      <AtomRichtext
        v-if="authorData.text"
        :string="authorData.text"
      />
      <AtomCta
        v-if="authorData.link"
        :link="authorData.link"
        :text="isArray ? useTranslation('general', 'more_of_store') : useTranslation('general', 'more_of_author')"
      />
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
    data: {
        type: Object,
        default: () => {},
    },
});

/*
    Setup
*/
const context = useContextStore();

/*
    Get strapi data
*/
const { find, findOne } = useStrapi4();

const authorInfo = ref(null);
const isArray = ref(false);
const isStore = props.data?.strapi_data_key?.includes('store');

const getAuthorInfo = async () => {
    const collection = context.getCollection;
    const collectionId = context.getId;
    let collectionColumn = null;
    if (collection === 'api::consultation.consultation') {
        collectionColumn = 'consultations';
    } else if (collection === 'api::event.event') {
        collectionColumn = 'events';
    }

    let author = null;
    const relatedKey = isStore ? 'stores' : 'speaker';
    if (collectionColumn && collectionId) {
        const currentData = await findOne(collectionColumn, collectionId, {
            populate: [relatedKey, `${relatedKey}.image`, !isStore && `${relatedKey}.block_author_image`],
        });

        author = currentData?.data?.attributes[relatedKey];
    } else {
        const slug = props.data.link?.cached_url;

        const collectionToFetch = slug.includes('experten') ? 'speakers' : 'stores';

        author = await find(collectionToFetch, {
            filters: {
                sb_slug: props.data.link?.cached_url,
            },
            populate: ['image', !isStore && 'block_author_image'],
        });
    }
    isArray.value = Array.isArray(author?.data) && author?.data.length > 1;
    authorInfo.value = isArray.value ? author?.data[0] : author?.data;
};

useLazyAsyncData(`author${getUid()}`, getAuthorInfo, {});

const authorData = computed(() => {
    if (isStore) {
        return {
            image: authorInfo.value?.attributes?.image?.data?.attributes?.url
             || props.data?.image?.filename,
            title: authorInfo.value?.attributes?.store_title || props.data?.title,
            text: authorInfo.value?.attributes?.store_lead || props.data?.text,
            link: authorInfo.value?.attributes?.sb_slug,
        };
    }

    return {
        image: authorInfo.value?.attributes?.block_author_image?.data?.attributes?.url
         || props.data?.image?.filename,
        title: authorInfo.value?.attributes?.intro_title || props.data?.title,
        text: authorInfo.value?.attributes?.block_author_text || props.data?.text,
        link: authorInfo.value?.attributes?.sb_slug,
    };
});
</script>

<style lang="scss">
.block-author {
    @include grid-columns(3, 14);
    @include grid-layout(14);
    @include fluid('margin-bottom', 30px, 116px);

    overflow: hidden;
    border-radius: 12px;
    background-color: $C_GREEN_BRIGHT;

    @include tablet{
        @include grid-columns(2, 10);
        @include grid-layout(10);
    }

    @include mobile {
        @include grid-layout();
    }
}

.block-author__image {
    @include grid-columns(1, 5);
    .atom-image {
        width: 100%;
        height: 100%;

        img {
            height: 100%;
            object-fit: cover;
        }
    }

    @include tablet {
        @include grid-columns(1, 4);
    }
}

.block-author__content {
    @include fluid('padding-top', 20px, 40px);
    @include grid-columns(6, 9);

    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-right: 87px;
    padding-bottom: 10px;
    padding-left: 50px;

    &.is-full-width {
        @include grid-columns(1, 14);
    }

    .atom-heading {
        @include fluid('margin-bottom', 10px, 20px);
    }

    .atom-richtext {
        @include fluid('margin-bottom', 10px, 20px);
    }

    .atom-cta {
        @include fluid('margin-bottom', 20px, 40px);
    }

    @include tablet {
        @include grid-columns(5, 6);

        padding-right: 30px;
        padding-left: 30px;
    }
}
</style>
